import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { getResult } from "../../../actions/admin";
import { getPMAssessmentById } from "../../../actions/assessment";
import ToolTipComp from "../../layouts/ToolTip";
import { Button, Modal } from "react-bootstrap";
import PMAssessment from "./PMAssessment";
import ProgressBarStatus from "../../layouts/PercentileBar";
import { set } from "lodash";

// --- Add above the component, or inside it above the return ---
const SUB_SKILL_SEQUENCE = [
  "Silly Word",
  "Break Up The Word",
  "Say The First Sound",
  "Letter Sounds",
  "Name the Letter",
];

const normalize = (str) => (str || "").trim().toLowerCase();
const GOOD_STATUSES = ["Above Benchmark", "At Benchmark"];
const BAD_STATUSES = ["Below Benchmark", "Well Below Benchmark"];
const getNextStepGuidance = (assessmentType, levelId, status) => {
  if (!assessmentType || !status) return null;

  const isGood = GOOD_STATUSES.includes(status);
  const isBad = BAD_STATUSES.includes(status);
  if (!isGood && !isBad) return null;

  const normalizedType = normalize(assessmentType);

  if (normalizedType === "story reading") {
    const level = Number(levelId);

    if (level >= 3) {
      return isGood
        ? "Test up 1 Level or Progress Monitor at this level"
        : "Test down 2 Levels";
    }
    if (level === 2) {
      return isGood
        ? "Test up 1 Level or Progress Monitor at this level"
        : "Test down 1 Level";
    }
    if (level === 1) {
      return isGood
        ? "Test up 1 Level or Progress Monitor at this level"
        : "Test down to Silly Words";
    }
    return null;
  }

  const skillIndex = SUB_SKILL_SEQUENCE.findIndex(
    (skill) => normalize(skill) === normalizedType,
  );

  if (skillIndex !== -1) {
    if (isGood) {
      return "Progress Monitor at this level";
    }
    const nextSkill = SUB_SKILL_SEQUENCE[skillIndex + 1];
    return nextSkill
      ? `Test down to ${nextSkill}`
      : "No further testing recommended.";
  }

  return null;
};

const PMResultsListItem = ({ result, getPMAssessmentById, done, setDone }) => {
  const [edit, setEdit] = useState(false);
  const [show, setShow] = useState(false);
  const [startAssessment, setStartAssessment] = useState(false);
  const [showNextSteps, setShowNextSteps] = useState(false);
  const [nextStepsIcon, setNextStepsIcon] = useState(false);
  const [viewAssessment, setViewAssessment] = useState(false);
  const {
    ResultID,
    AccessCode,
    Title,
    LevelID,
    AssessmentType,
    CategoryID,
    ProbeID,
    TotalCLS,
    TotalWWR,
    riskStatus,
    wellBelowBenchmarkMin,
    wellBelowBenchmarkMax,
    belowBenchmarkMin,
    belowBenchmarkMax,
    atBenchmarkMin,
    atBenchmarkMax,
    aboveBenchmarkMin,
    aboveBenchmarkMax,
    TotalScore,
    CreatedDate,
  } = result;
  const toRangeString = (min, max) => `${min ?? 0} to ${max ?? 0}`;

  const getBenchmarkStatus = () => {
    const numericStatus = Number(riskStatus);
    if (!Number.isNaN(numericStatus)) {
      if (numericStatus === 1) return "Above Benchmark";
      if (numericStatus === 2) return "At Benchmark";
      if (numericStatus === 3) return "Below Benchmark";
      if (numericStatus === 4) return "Well Below Benchmark";
    }

    if (typeof riskStatus === "string") {
      const normalized = riskStatus.trim().toLowerCase();
      if (normalized === "above benchmark") return "Above Benchmark";
      if (normalized === "at benchmark") return "At Benchmark";
      if (normalized === "below benchmark") return "Below Benchmark";
      if (normalized === "well below benchmark") return "Well Below Benchmark";
    }
    const score = Number(TotalScore);
    if (!Number.isNaN(score)) {
      if (
        score >= Number(aboveBenchmarkMin) &&
        score <= Number(aboveBenchmarkMax)
      ) {
        return "Above Benchmark";
      }
      if (score >= Number(atBenchmarkMin) && score <= Number(atBenchmarkMax)) {
        return "At Benchmark";
      }
      if (
        score >= Number(belowBenchmarkMin) &&
        score <= Number(belowBenchmarkMax)
      ) {
        return "Below Benchmark";
      }
      if (
        score >= Number(wellBelowBenchmarkMin) &&
        score <= Number(wellBelowBenchmarkMax)
      ) {
        return "Well Below Benchmark";
      }
    }

    return "";
  };

  const getRefInfo = (resultRef) => {
    if (resultRef && resultRef !== "") {
      const parsedRef = JSON.parse(resultRef);
      return {
        ResultID: parsedRef.ResultID,
        AssessmentSkill: parsedRef.AssessmentSkill,
      };
    }
    return null;
  };

  // Helper function to get benchmark status based on score and benchmark ranges
  const getBenchmarkStatusForScore = (
    score,
    wellMin,
    wellMax,
    belowMin,
    belowMax,
    atMin,
    atMax,
    aboveMin,
    aboveMax,
  ) => {
    const numScore = Number(score);
    if (numScore >= Number(aboveMin) && numScore <= Number(aboveMax)) {
      return "Above Benchmark";
    }
    if (numScore >= Number(atMin) && numScore <= Number(atMax)) {
      return "At Benchmark";
    }
    if (numScore >= Number(belowMin) && numScore <= Number(belowMax)) {
      return "Below Benchmark";
    }
    if (numScore >= Number(wellMin) && numScore <= Number(wellMax)) {
      return "Well Below Benchmark";
    }
    return "";
  };

  const benchmarkStatus = getBenchmarkStatus();
  const nextStepGuidance =
    TotalScore !== null
      ? getNextStepGuidance(AssessmentType, LevelID, benchmarkStatus)
      : null;
  const hist = useHistory();
  const prevTotalScoreRef = useRef(TotalScore);

  // Extract secondary result if it exists (combined data from PMResultsList)
  const secondaryResult = result._secondary || null;
  const primaryResultID = ResultID;
  useEffect(() => {
    const wasNull = prevTotalScoreRef.current === null;
    const isNowSet = TotalScore !== null;

    if (wasNull && isNowSet && nextStepGuidance) {
      setShowNextSteps(true);
    }

    prevTotalScoreRef.current = TotalScore;
  }, [TotalScore, nextStepGuidance]);
  return (
    <>
      <div className="admin-users-fields  d-flex align-items-center justify-content-around p-2 border-bottom">
        {/* Assessment Level */}
        <div
          className="admin-users-field  text-truncate d-md-block d-none"
          title={"Level " + (LevelID == 0 ? "K" : LevelID)}
        >
          {"Level " + (LevelID == 0 ? "K" : LevelID)}
        </div>

        {/* Assessment Type */}
        <div
          className="admin-assessments-field  text-truncate d-md-block d-none"
          title={AssessmentType}
        >
          {AssessmentType}
        </div>

        {/* Assessment Title */}
        <div
          className="admin-users-field  text-truncate d-md-block d-none"
          title={Title}
        >
          {Title}
        </div>

        {/* ProbeID */}
        <div
          className="admin-probe-field  text-truncate d-md-block d-none"
          title={ProbeID}
        >
          {ProbeID}
        </div>

        {/* Risk Status */}
        {TotalScore !== null ? (
          <div
            className="admin-riskstatus-field text-truncate d-md-block d-none py-3"
            title={riskStatus}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              paddingRight: "2rem",
            }}
          >
            {/* Primary Score */}
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <span
                style={{ fontSize: "0.85rem", color: "#666", minWidth: "40px" }}
              >
                {CategoryID === 4
                  ? "CLS:"
                  : CategoryID === 3
                    ? "TWC:"
                    : "Score:"}
              </span>
              <ProgressBarStatus
                calculationType={2}
                rawScore={TotalScore}
                status={benchmarkStatus}
                wellBelowBenchmarkRange={toRangeString(
                  result.wellBelowBenchmarkMin ?? wellBelowBenchmarkMin,
                  result.wellBelowBenchmarkMax ?? wellBelowBenchmarkMax,
                )}
                belowBenchmarkRange={toRangeString(
                  result.belowBenchmarkMin ?? belowBenchmarkMin,
                  result.belowBenchmarkMax ?? belowBenchmarkMax,
                )}
                atBenchmarkRange={toRangeString(
                  result.atBenchmarkMin ?? atBenchmarkMin,
                  result.atBenchmarkMax ?? atBenchmarkMax,
                )}
                aboveBenchmarkRange={toRangeString(
                  result.aboveBenchmarkMin ?? aboveBenchmarkMin,
                  result.aboveBenchmarkMax ?? aboveBenchmarkMax,
                )}
              />
            </div>
            {/* Secondary Score if available */}
            {secondaryResult && secondaryResult.TotalScore !== null && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: "#666",
                    minWidth: "40px",
                  }}
                >
                  {CategoryID === 4 ? "WWR:" : "Acc:"}
                </span>
                <ProgressBarStatus
                  calculationType={2}
                  rawScore={secondaryResult.TotalScore}
                  status={getBenchmarkStatusForScore(
                    secondaryResult.TotalScore,
                    secondaryResult.wellBelowBenchmarkMin ??
                      wellBelowBenchmarkMin,
                    secondaryResult.wellBelowBenchmarkMax ??
                      wellBelowBenchmarkMax,
                    secondaryResult.belowBenchmarkMin ?? belowBenchmarkMin,
                    secondaryResult.belowBenchmarkMax ?? belowBenchmarkMax,
                    secondaryResult.atBenchmarkMin ?? atBenchmarkMin,
                    secondaryResult.atBenchmarkMax ?? atBenchmarkMax,
                    secondaryResult.aboveBenchmarkMin ?? aboveBenchmarkMin,
                    secondaryResult.aboveBenchmarkMax ?? aboveBenchmarkMax,
                  )}
                  wellBelowBenchmarkRange={toRangeString(
                    secondaryResult.wellBelowBenchmarkMin ??
                      wellBelowBenchmarkMin,
                    secondaryResult.wellBelowBenchmarkMax ??
                      wellBelowBenchmarkMax,
                  )}
                  belowBenchmarkRange={toRangeString(
                    secondaryResult.belowBenchmarkMin ?? belowBenchmarkMin,
                    secondaryResult.belowBenchmarkMax ?? belowBenchmarkMax,
                  )}
                  atBenchmarkRange={toRangeString(
                    secondaryResult.atBenchmarkMin ?? atBenchmarkMin,
                    secondaryResult.atBenchmarkMax ?? atBenchmarkMax,
                  )}
                  aboveBenchmarkRange={toRangeString(
                    secondaryResult.aboveBenchmarkMin ?? aboveBenchmarkMin,
                    secondaryResult.aboveBenchmarkMax ?? aboveBenchmarkMax,
                  )}
                />
              </div>
            )}
          </div>
        ) : (
          <div
            className="admin-users-field  text-truncate d-md-block d-none"
            title={"N/A"}
          >
            N/A
          </div>
        )}

        {/* Created Date */}
        <div
          className="admin-users-field text-truncate ml-1"
          title={CreatedDate}
        >
          {CreatedDate
            ? new Date(CreatedDate).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "N/A"}
        </div>

        {/* Action  */}
        <div className="admin-users-field  text-truncate d-flex">
          {AccessCode === null ? (
            <>
              <ToolTipComp
                myButton={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="24px"
                    viewBox="0 0 24 24"
                    width="24px"
                    fill={edit ? "#18a587" : "#505050"}
                    onMouseEnter={() => {
                      setEdit(true);
                    }}
                    onMouseLeave={() => {
                      setEdit(false);
                    }}
                    className="cursor-pointer"
                    onClick={() => {
                      getPMAssessmentById(primaryResultID).then(() => {
                        setStartAssessment(true);
                        setViewAssessment(false);
                        setDone(false);
                      });
                    }}
                  >
                    <path d="M0 0h24v24H0V0z" fill="none" />
                    <path d="M3 17.46v3.04c0 .28.22.5.5.5h3.04c.13 0 .26-.05.35-.15L17.81 9.94l-3.75-3.75L3.15 17.1c-.1.1-.15.22-.15.36zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                  </svg>
                }
                type="Edit"
                module="Assessment Result"
              />
            </>
          ) : (
            AccessCode && (
              <ToolTipComp
                myButton={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="24"
                    viewBox="0 0 16 16"
                    width="24"
                    fill={edit ? "#18a587" : "#505050"}
                    onMouseEnter={() => {
                      setEdit(true);
                    }}
                    onMouseLeave={() => {
                      setEdit(false);
                    }}
                    className="bi bi-play-fill cursor-pointer"
                    onClick={() => {
                      getPMAssessmentById(primaryResultID).then(() => {
                        setStartAssessment(true);
                        setViewAssessment(false);
                        setDone(false);
                      });
                    }}
                  >
                    <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z" />
                  </svg>
                }
                type="Start"
                module="Assessment"
              />
            )
          )}
          <ToolTipComp
            myButton={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                enable-background="new 0 0 24 24"
                height="24px"
                viewBox="0 0 24 24"
                width="24px"
                className="mx-3 cursor-pointer"
                fill={show ? "#18a587" : "#505050"}
                onMouseEnter={() => {
                  setShow(true);
                }}
                onMouseLeave={() => {
                  setShow(false);
                }}
                onClick={() => {
                  getPMAssessmentById(primaryResultID).then(() => {
                    setStartAssessment(true);
                    // setDone(true);
                    setViewAssessment(true);
                  });
                }}
              >
                <g>
                  <path d="M0,0h24v24H0V0z" fill="none" />
                </g>
                <g>
                  <g>
                    <path d="M15,3H5C3.9,3,3.01,3.9,3.01,5L3,19c0,1.1,0.89,2,1.99,2H19c1.1,0,2-0.9,2-2V9L15,3z M5,19V5h9v5h5v9H5z M9,8 c0,0.55-0.45,1-1,1S7,8.55,7,8s0.45-1,1-1S9,7.45,9,8z M9,12c0,0.55-0.45,1-1,1s-1-0.45-1-1s0.45-1,1-1S9,11.45,9,12z M9,16 c0,0.55-0.45,1-1,1s-1-0.45-1-1s0.45-1,1-1S9,15.45,9,16z" />
                  </g>
                </g>
              </svg>
            }
            type="Show"
            module="Assessment Result"
          />
          {nextStepGuidance && (
            <ToolTipComp
              myButton={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="24px"
                  viewBox="0 0 24 24"
                  width="24px"
                  className="mx-3 cursor-pointer"
                  fill={nextStepsIcon ? "#18a587" : "#505050"}
                  onMouseEnter={() => setNextStepsIcon(true)}
                  onMouseLeave={() => setNextStepsIcon(false)}
                  onClick={() => setShowNextSteps(true)}
                >
                  <path d="M0 0h24v24H0V0z" fill="none" />
                  <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                </svg>
              }
              type="Next Steps"
              module="Assessment Result"
            />
          )}
          {/* {((CategoryID == 4 && !((getRefInfo(result.ResultRef)?.AssessmentSkill) ===
            "WWR")) || (CategoryID != 4))&& (
            <ToolTipComp
              myButton={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  enable-background="new 0 0 24 24"
                  height="24px"
                  viewBox="0 0 24 24"
                  width="24px"
                  className="mx-3 cursor-pointer"
                  fill={show ? "#18a587" : "#505050"}
                  onMouseEnter={() => {
                    setShow(true);
                  }}
                  onMouseLeave={() => {
                    setShow(false);
                  }}
                  onClick={() => {
                    // getResult(result.ResultID).then(() =>
                    //   hist.push(`/admin/result/resultId=${result.ResultID}`),
                    // );
                  }}
                >
                  <g>
                    <path d="M0,0h24v24H0V0z" fill="none" />
                  </g>
                  <g>
                    <g>
                      <path d="M15,3H5C3.9,3,3.01,3.9,3.01,5L3,19c0,1.1,0.89,2,1.99,2H19c1.1,0,2-0.9,2-2V9L15,3z M5,19V5h9v5h5v9H5z M9,8 c0,0.55-0.45,1-1,1S7,8.55,7,8s0.45-1,1-1S9,7.45,9,8z M9,12c0,0.55-0.45,1-1,1s-1-0.45-1-1s0.45-1,1-1S9,11.45,9,12z M9,16 c0,0.55-0.45,1-1,1s-1-0.45-1-1s0.45-1,1-1S9,15.45,9,16z" />
                    </g>
                  </g>
                </svg>
              }
              type="Show"
              module="Assessment Result"
            />)} */}
        </div>
      </div>

      {/* Modal- Sub module */}
      <Modal
        show={startAssessment}
        onHide={() => setStartAssessment(false)}
        centered
        size="xl"
      >
        <Modal.Header closeButton></Modal.Header>

        <Modal.Body>
          <PMAssessment
            resultID={ResultID}
            setStartAssessment={setStartAssessment}
            done={done}
            setDone={setDone}
            viewAssessment={viewAssessment}
          />
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setStartAssessment(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal - Next Steps guidance */}
      <Modal
        show={showNextSteps}
        onHide={() => setShowNextSteps(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Next Assessment Recommended</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div
            className="d-flex align-items-center justify-content-between rounded px-3 py-2"
            style={{ backgroundColor: "#e8f7f3", border: "1px solid #18a587" }}
          >
            <div>
              <span className="fw-semibold">Next assessment recommended:</span>{" "}
              {nextStepGuidance}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="button-primary"
            onClick={() => setShowNextSteps(false)}
          >
            Continue
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

PMResultsListItem.propTypes = {
  user: PropTypes.object.isRequired,
  getResult: PropTypes.func.isRequired,
};

export default connect(null, { getResult, getPMAssessmentById })(
  PMResultsListItem,
);
